import { useEffect, useState } from "react";
import { API_CACHE_TTL, API_MISS_TTL, fetchCachedGetApi, isAbortError } from "@/utils/apiCache";

interface UseGetNewestTrackProps {
  artistName: string;
}

export interface UseGetNewestTrackOptions {
  enabled?: boolean;
}

interface SpotifyTrack {
  id: string;
  name: string;
  preview_url: string | null;
  artists: Array<{
    id: string;
    name: string;
    external_urls?: { spotify: string };
  }>;
  album: {
    id: string;
    name: string;
    images: Array<{
      url: string;
      height: number;
      width: number;
    }>;
    release_date: string;
    external_urls?: { spotify: string };
  };
  external_urls?: { spotify: string };
}

interface SpotifyRelease {
  id: string;
  name: string;
  images: Array<{ url: string }>;
  release_date: string;
  album_type: string;
  external_urls: { spotify: string };
  artists: Array<{
    name: string;
    external_urls: { spotify: string };
  }>;
}

export interface SpotifyNewestTrackResponse {
  artistProfileUrl?: string;
  previewUrl?: string;
  newestReleases?: SpotifyRelease[];
  trackData?: SpotifyTrack;
  error?: string;
}

function useGetNewestTrack(
  props: UseGetNewestTrackProps,
  options?: UseGetNewestTrackOptions,
  dependencies: React.DependencyList = []
): SpotifyNewestTrackResponse | null {
  const [artistData, setArtistData] = useState<SpotifyNewestTrackResponse | null>(null);
  const { artistName } = props;

  async function fetchSpotifyNewestTrack(artistId: string, signal?: AbortSignal): Promise<SpotifyNewestTrackResponse> {
    if (typeof window !== "undefined") {
      return fetchCachedGetApi<SpotifyNewestTrackResponse>("/.netlify/functions/SpotifyGetNewestRelease", { artist: artistId }, {
        ttlMs: API_CACHE_TTL.SIX_HOURS,
        missTtlMs: API_MISS_TTL.ONE_DAY,
        shouldCache: (data) => Boolean((data?.trackData || data?.newestReleases?.length) && !data.error),
        signal,
      });
    }
    return { error: "Window not available" };
  }

  useEffect(() => {
    if (typeof window === "undefined" || !artistName) return;

    const controller = new AbortController();

    fetchSpotifyNewestTrack(artistName, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setArtistData(data);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        console.log("err:", err);
        setArtistData({ error: err.message });
      });

    return () => controller.abort();
  }, [...dependencies]);

  if (!artistName) {
    return null;
  }

  return artistData;
}

export default useGetNewestTrack;
